import React from "react";
import {autobind} from "core-decorators";
import {Input, Switch, Table, Tooltip, Typography} from "antd";
import type {TablePaginationConfig} from "antd/es/table";
import type {FilterValue} from "antd/es/table/interface";
import {InfoCircleOutlined, LinkOutlined, SearchOutlined} from "@ant-design/icons";
import {RiBook3Line} from "react-icons/ri";
import {action, observable, reaction} from "mobx";
import {disposeOnUnmount, inject, observer} from "mobx-react";
import {gql} from "@apollo/client";
import {RouteComponentProps} from "react-router-dom";
import _debounce from "lodash/debounce";
import _isEmpty from "lodash/isEmpty";

import "./Catalog.scss";
import {CatalogQuery} from "@/graphql/types";
import {ChoirIds, LanguageKeys, Languages} from "@/shared/constants";
import CatalogActions from "./CatalogActions";
import PageLayout from "@/components/PageLayout/PageLayout";
import Store from "@/stores";

export interface ICatalogProps extends RouteComponentProps {
  store?: Store;
}

const DEFAULT_PAGE_SIZE = 10;

@inject("store")
@autobind
@observer
export default class Catalog extends React.Component<ICatalogProps> {
  @observable languages?: String[];
  @observable loadingPieces: boolean = true;
  @observable pagination: TablePaginationConfig = {current: 1, pageSize: DEFAULT_PAGE_SIZE};
  @observable pieces?: CatalogQuery["pieces"];
  @observable searchText: string = "";
  @observable showCommunityPieces: boolean = false;

  async componentDidMount() {
    // every time the "show community pieces switch" changes
    disposeOnUnmount(
      this,
      reaction(
        () => this.showCommunityPieces,
        async () => await this.fetchPiecesFromFirstPage(),
      ),
    );
    // every time search text changes
    const fetchPiecesFromFirstPageDebounced = _debounce(this.fetchPiecesFromFirstPage, 500);
    disposeOnUnmount(
      this,
      reaction(() => this.searchText, fetchPiecesFromFirstPageDebounced),
    );
    // fetch pieces
    await this.fetchPieces();
  }

  async fetchPieces() {
    const store = this.props.store!;
    // Do not fetch if we have signed out
    if (!store.session.isSignedIn) return;
    this.setLoadingPieces(true);
    const {current: page = 1, pageSize = DEFAULT_PAGE_SIZE} = this.pagination;
    const choirIds = [ChoirIds.catalog];
    if (this.showCommunityPieces) choirIds.push(ChoirIds.community); // 759 is the
    // https://www.apollographql.com/docs/react/api/apollo-client/#ApolloClient.query
    const result = await store.apollo.client.query<CatalogQuery>({
      query: gql`
        query CatalogQuery(
          $ensembleId: Int!
          $choirIds: [Int!]!
          $search: String
          $languages: [String!]
          $offset: Int!
          $limit: Int!
        ) {
          pieces(
            where: {
              choir_id: {_in: $choirIds}
              status: {_eq: "published"}
              _or: [{title: {_ilike: $search}}, {composer: {_ilike: $search}}]
              language: {_in: $languages}
              deleted: {_eq: false}
              active_version: {_eq: true}
            }
            order_by: [{composer: asc}, {title: asc}]
            offset: $offset
            limit: $limit
          ) {
            id
            title
            composer
            language
            choir_id
            source_url
            catalog_pieces(
              limit: 1
              where: {choir_id: {_eq: $ensembleId}, deleted: {_eq: false}, active_version: {_eq: true}}
            ) {
              id
            }
          }
          pieces_aggregate(
            where: {
              choir_id: {_in: $choirIds}
              status: {_eq: "published"}
              _or: [{title: {_ilike: $search}}, {composer: {_ilike: $search}}]
              language: {_in: $languages}
            }
          ) {
            aggregate {
              count
            }
          }
        }
      `,
      variables: {
        ensembleId: store.session.selectedEnsemble?.id!,
        choirIds,
        search: _isEmpty(this.searchText) ? undefined : `%${this.searchText}%`,
        languages: this.languages,
        limit: pageSize,
        offset: pageSize * (page - 1),
      },
    });
    this.setPieces(result.data?.pieces);
    this.setPagination({...this.pagination, total: result.data?.pieces_aggregate.aggregate?.count});
    this.setLoadingPieces(false);
  }

  async fetchPiecesFromFirstPage() {
    this.setPagination({...this.pagination, current: 1});
    await this.fetchPieces();
  }

  onSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    this.setSearchText(event.target.value || "");
  }

  async onTableChange(pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>) {
    this.setPagination(pagination);
    const languages = filters?.language as String[] | undefined | null;
    this.setLanguages(languages || undefined);
    await this.fetchPieces();
  }

  @action
  setLanguages(languages?: String[]) {
    this.languages = languages;
  }

  @action
  setLoadingPieces(loading: boolean) {
    this.loadingPieces = loading;
  }

  @action
  setPagination(pagination: TablePaginationConfig) {
    this.pagination = pagination;
  }

  @action
  setPieces(pieces: CatalogQuery["pieces"]) {
    this.pieces = pieces;
  }

  @action
  setSearchText(text: string) {
    this.searchText = text;
  }

  @action
  setShowCommunityPieces(show: boolean) {
    this.showCommunityPieces = show;
  }

  toggleShowCommunityPieces() {
    this.setShowCommunityPieces(!this.showCommunityPieces);
  }

  renderLanguageColumn(text: any, record: CatalogQuery["pieces"][0], index: number): React.ReactNode {
    return text.charAt(0).toUpperCase() + text.slice(1); // Upper case for the first letter
  }

  renderSourceColumn(text: any, record: CatalogQuery["pieces"][0], index: number): React.ReactNode {
    if (record.choir_id === ChoirIds.catalog) return "catalog";
    else if (record.choir_id === ChoirIds.community)
      return (
        <span>
          community
          {record.source_url && (
            <>
              {" "}
              <Tooltip arrowPointAtCenter title="Link to original score">
                <a href={record.source_url} target="_blank" rel="external noreferrer">
                  <LinkOutlined />
                </a>
              </Tooltip>
            </>
          )}
        </span>
      );
    return "unknown";
  }

  public render() {
    return (
      <PageLayout>
        <div className="Catalog">
          <Typography.Title level={3}>
            <RiBook3Line className="anticon title-icon" />
            Catalog
          </Typography.Title>
          <div className="search-wrapper">
            <Input
              className="search-input"
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={this.searchText}
              onChange={this.onSearchChange}
            />
            <span className="search-community">
              <Switch onChange={this.toggleShowCommunityPieces} checked={this.showCommunityPieces} />
              <span>Include community pieces</span>
              <Tooltip
                arrowPointAtCenter
                title={
                  <span>
                    Community pieces are contributions to the{" "}
                    <a href="https://cpdl.org" target="_blank" rel="external noreferrer">
                      Choral Public Domain Library
                    </a>
                    . Find the link to the original score in the source column
                  </span>
                }
              >
                <InfoCircleOutlined />
              </Tooltip>
            </span>
          </div>
          <Table<CatalogQuery["pieces"][0]>
            dataSource={this.pieces}
            rowKey="id"
            loading={this.loadingPieces}
            pagination={this.pagination}
            onChange={this.onTableChange}
            scroll={{x: true}}
          >
            <Table.Column<CatalogQuery["pieces"][0]>
              title="Actions"
              key="actions"
              render={(_, piece) => <CatalogActions piece={piece} />}
            />
            <Table.Column<CatalogQuery["pieces"][0]> title="Composer" dataIndex="composer" key="composer" />
            <Table.Column<CatalogQuery["pieces"][0]> title="Title" dataIndex="title" key="title" />
            <Table.Column<CatalogQuery["pieces"][0]>
              title="Language"
              dataIndex="language"
              key="language"
              render={this.renderLanguageColumn}
              filters={LanguageKeys.map((key) => ({value: key, text: Languages[key]}))}
              filterMultiple
            />
            {this.showCommunityPieces && (
              <Table.Column<CatalogQuery["pieces"][0]>
                title="Source"
                dataIndex="source"
                key="source"
                render={this.renderSourceColumn}
              />
            )}
          </Table>
        </div>
      </PageLayout>
    );
  }
}
